<template>
  <div class="tooltip-container" @mouseenter="!disabled && (showTooltip = true)" @mouseleave="showTooltip = false">
    <slot />
    <Transition name="tooltip">
      <div v-if="showTooltip" class="tooltip" :class="position">
        <div class="tooltip-content">
          <div v-if="title" class="tooltip-title">{{ title }}</div>
          <div class="tooltip-text">{{ text }}</div>
        </div>
        <div class="tooltip-arrow" :class="position"></div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Props {
  title?: string
  text: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  position: 'bottom',
  disabled: false
})

const showTooltip = ref(false)

defineOptions({ name: 'InfoTooltip' })
</script>

<style scoped lang="scss">
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  z-index: 9999;
  pointer-events: none;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));

  &.bottom {
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  }

  &.top {
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  }

  &.left {
    right: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
  }

  &.right {
    left: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
  }
}

.tooltip-content {
  background: linear-gradient(135deg, #1a1f2e, #242b3d);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 12px;
  max-width: 320px;
  min-width: 240px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);

  .tooltip-title {
    font-weight: 700;
    font-size: 13px;
    color: #ffffff;
    margin-bottom: 4px;
    line-height: 1.3;
  }

  .tooltip-text {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;

  &.bottom {
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #1a1f2e;
  }

  &.top {
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #1a1f2e;
  }

  &.left {
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid #1a1f2e;
  }

  &.right {
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #1a1f2e;
  }
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

.tooltip-enter-to,
.tooltip-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

// Position-specific enter/leave transforms
.tooltip.top {
  &.tooltip-enter-from,
  &.tooltip-leave-to {
    transform: translateX(-50%) translateY(4px);
  }
}

.tooltip.left {
  &.tooltip-enter-from,
  &.tooltip-leave-to {
    transform: translateY(-50%) translateX(4px);
  }

  &.tooltip-enter-to,
  &.tooltip-leave-from {
    transform: translateY(-50%) translateX(0);
  }
}

.tooltip.right {
  &.tooltip-enter-from,
  &.tooltip-leave-to {
    transform: translateY(-50%) translateX(-4px);
  }

  &.tooltip-enter-to,
  &.tooltip-leave-from {
    transform: translateY(-50%) translateX(0);
  }
}
</style>